/* eslint-disable react/no-unescaped-entities */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import React, { useCallback, useState } from "react";

import axios from "axios";

import { useMsal } from "@azure/msal-react";

import { b2cPolicies, deployment } from "../../../authConfig";
import { useEffect, useMemo } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_EditActionButtons,
  MRT_EditCellTextField,
  MRT_Row,
  MRT_ShowHideColumnsButton,
  MRT_TableOptions,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import * as Yup from "yup";
import { useFormik } from "formik";
import { findSigninAccount } from "../../../helpers/hooks/useAccountFinder";
import { jsPDF } from "jspdf"; //or use your library of choice here
import autoTable from "jspdf-autotable";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { CSVLink, CSVDownload } from "react-csv";
import Link from "@mui/material/Link";
import DocViewer from "../../../helpers/docViewerCustom";
import { DocViewerRenderers } from "../../../helpers/docViewerCustom/renderers";
import { makeStyles, createStyles } from "@mui/styles";
import { Document, Page } from "react-pdf";
import { pdfjs } from "react-pdf";
import "./styles.css";
import { RRM_EditCellTextField } from "../../../helpers/TextField";
import { ReactComponent as Export } from "../../../assets/export_notes.svg";
import { ReactComponent as Edit } from "../../../assets/open_in_new.svg";
import { ReactComponent as MoreOption } from "../../../assets/more_vert.svg";
import { ReactComponent as Upload } from "../../../assets/cloud_upload.svg";
import { ReactComponent as Delete } from "../../../assets/delete.svg";
import { RRM_EditActionButtons } from "../../../helpers/Button";
import Dropzone from "react-dropzone";
import ViewAction from "../ViewAction";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { ContactPhone } from "@mui/icons-material";

// customised tooltip
const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#F8F8FF",
    color: "#000706",
    fontSize: 14,
    width: "270px",
    maxWidth: "none",
    // height: "40px",
    maxHeight: "none",
    boxShadow: "0px 4px 16px 0px #0000004D",
    padding: "8px",
  },
}));
type IdTokenClaims = {
  roles: string[];
  idp: string;
  email: string;
  signInName: string;
  appTenantName: string;
  sub: string;
  appTenantId: string;
  QuestionId: string;
  acr: any;
};
//example data type
type ServiceProviderRegister = {
  id: string;
  tenantId: string;
  supplierName: string;
  serviceType: string;
  serviceDetails: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  dateServicesCommenced: Date;
  dateContractSigned: Date;
  contractDocument: string;
  otherVendorInformation: string;
  isDeleted: boolean;
  fileUpload: string;
  otherServiceType: string;
};
const extractedHeaders = {
  "Supplier Name": "supplierName",
  "Supplier Service Type": "serviceType",
  "Details of Services Provided": "serviceDetails",
  "Contact Name": "contactName",
  "Contact Email": "contactEmail",
  "Contact Phone": "contactPhone",
  "Date Service Commenced": "dateServicesCommenced",
  "Date Contract Signed": "dateContractSigned",
  "Contract Document": "contractDocument",
  "Other Relevant Information": "otherVendorInformation",
};
interface FormErrors {
  supplierName?: string;
  serviceType?: string;
  serviceDetails?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  dateServicesCommenced?: Date;
  dateContractSigned?: Date;
  contractDocument?: string;
  otherVendorInformation?: string;
  lastUpdateDate?: Date;
  otherServiceType?: string;
  // Add other fields as needed
}
function getModalStyle() {
  const top = 50;
  const left = 50;
  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}
const useStyles = makeStyles(() => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    position: "absolute",
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    overflow: "hidden",
    height: "100%",
    width: "100%",
  },
  iframe: {
    display: "block",
    "border-style": "none",
    width: "100px",
    height: "50px",
    margin: "auto",
    "background-color": "#777",
  },
}));
const queryClient = new QueryClient();
const defaultStatus = [
  {
    label: "Allocated",
    value: "Allocated",
  },
  {
    label: "Pending Allocation",
    value: "Pending Allocation",
  },
  {
    label: "Decommissioned",
    value: "Decommissioned",
  },
  {
    label: "Lost",
    value: "Lost",
  },
];
const defaultServiceType = [
  {
    label: "Hardware Vendor",
    value: "Hardware Vendor",
  },
  {
    label: "I.T. Support Services Provider",
    value: "I.T. Support Services Provider",
  },
  {
    label: " Internet Services Provider",
    value: " Internet Services Provider",
  },
  {
    label: "Other",
    value: "Other",
  },
];
const options = {
  cMapUrl: "cmaps/",
  cMapPacked: true,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts`,
};
function base64ToBlob(base64String: string, contentType = "") {
  const byteCharacters = atob(base64String);
  const byteArrays = [];

  for (let i = 0; i < byteCharacters.length; i++) {
    byteArrays.push(byteCharacters.charCodeAt(i));
  }

  const byteArray = new Uint8Array(byteArrays);
  return new Blob([byteArray], { type: contentType });
}
const ServiceProviderRegister = () => {
  //console.log("Members: " + props);

  const [ServiceProviderRegisters, setServiceProviderRegisters] = useState<
    ServiceProviderRegister[]
  >([]);
  const classes = useStyles();
  const [modalStyle] = useState(getModalStyle);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState<Blob>();
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageScale, setPageScale] = useState(1);
  const [data, setData] = useState<ServiceProviderRegister[]>([]);
  const [docs, setDocs] = useState<{ uri: string; fileName: string }[]>([]);
  const [fileType, setFileType] = useState<string>("");
  // const [files, setFiles] = useState<any>([]);
  const [documentUri, setDocumentUri] = useState<string>("");
  const { instance } = useMsal();
  const accounts = findSigninAccount(instance.getAllAccounts());
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [users, setUsers] = React.useState<Member[]>([]);
  const [file, setFile] = useState<any>();
  const [serviceType, setServiceType] = useState<string>("");
  const [deleteRowId, setDeleteRowId] = useState<string>("");
  var mammoth = require("mammoth/mammoth.browser");
  const validateRequired = (value: string) => {
    if (value === null || value === undefined) {
      return false;
    }
    return !!value.length;
  };
  const validatePhone = (phone: string) => {
    return !!phone.length && phone.length === 10 && phone.match(/^[0-9]+$/) !== null;
  };
  const validateEmail = (email: string) =>
    !!email.length &&
    email
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
  }

  const handleViewerClose = () => {
    setViewerOpen(false);
  };

  function handleZoomIn() {
    if (pageScale < 3) {
      setPageScale(pageScale + 0.2);
    }
  }

  function handleZoomOut() {
    if (pageScale > 0.3) {
      setPageScale(pageScale - 0.2);
    }
  }

  function handleNext() {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  }
  function handlePrevious() {
    if (pageNumber > 0) {
      setPageNumber(pageNumber - 1);
    }
  }

  function validateItem(item: ServiceProviderRegister) {
    console.log("item", item);
    const currentDate = new Date().toISOString().split("T")[0]; // Get today's date in ISO format
    console.log("currentDate", currentDate);

    return {
      serviceType: validateRequired(item.serviceType)
        ? undefined
        : "Supplier Service Type is required",
      supplierName: validateRequired(item.supplierName) ? undefined : "Supplier Name is required",
      serviceDetails: validateRequired(item.serviceDetails)
        ? undefined
        : "Details of Services Provided is required",
      contactName: validateRequired(item.contactName) ? undefined : "Contact Name is required",
      // contactEmail: validateEmail(item.contactEmail) ? undefined : "Invalid email",
      contactEmail: !validateRequired(item.contactEmail)
        ? "Contact Email is required"
        : !validateEmail(item.contactEmail)
        ? "Invalid email"
        : undefined,
      contactPhone: !validateRequired(item.contactPhone)
        ? "Contact Phone is required"
        : !validatePhone(item.contactPhone)
        ? "Contact Phone no. is not valid"
        : undefined,
      // contactPhone: validatePhone(item.contactPhone) ? undefined : "Invalid Phone Number",
      dateServicesCommenced: validateRequired(item.dateServicesCommenced.toString())
        ? undefined
        : "Date Service Commenced is required",
      // dateContractSigned: validateRequired(item.dateContractSigned.toString())
      //   ? undefined
      //   : "Date Contract Signed is required",
      dateContractSigned: !validateRequired(item.dateContractSigned.toString())
        ? "Date Contract Signed is required"
        : new Date(item.dateContractSigned).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)
        ? "Date cannot be in the future"
        : undefined,

      otherServiceType:
        item.serviceType === "Other"
          ? validateRequired(item.otherServiceType)
            ? undefined
            : "Other Service Type required"
          : undefined,
      //fileUpload check if the file extension is either .docx, .doc, .pdf
      //fileUpload: validateFileExtension(item.fileUpload) ? undefined : "Invalid file format",
      //contractDocument: validateRequired(item.contractDocument) ? undefined : "Required",
      //otherVendorInformation: validateRequired(item.otherVendorInformation)
      //  ? undefined
      //  : "Required",
    };
  }
  function validateFileExtension(file: string) {
    const fileExtension = file.split(".").pop();
    return ["docx", "doc", "pdf", "bmp", "jpg", "jpeg", "png", "csv"].includes(
      fileExtension as string
    );
  }
  const handleOrgClick = () => {
    window.location.href = "/get-started";
  };
  const handleClick = async (
    contractDocument: string,
    tenantId: string,
    referenceNumber: string
  ) => {
    let response = await axios.get(
      `${process.env.REACT_APP_API_SERVER}/rm/b2cpolicies/getFile?tenantId=` +
        tenantId +
        "&fileName=" +
        encodeURIComponent(contractDocument) +
        "&referenceNumber=" +
        referenceNumber
    );
    //remove the data:application/pdf;base64, prefix from response.data
    // Split the string by comma to separate content type and base64 string
    const parts = response.data.split(",");

    if (parts.length === 2) {
      // Extract content type and base64 string
      const contentType = parts[0].split(":")[1].split(";")[0];
      const base64String = parts[1];
      console.log("contentType", contentType);
      // Use contentType and base64String as needed
      const reader = new FileReader();
      if (
        contentType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        contentType === "application/msword" ||
        contentType === "application/vnd.ms-powerpoint" ||
        contentType ===
          "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
        contentType === "application/vnd.ms-excel" ||
        contentType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        //contractDocument.includes(".docx") ||
        //contractDocument.includes(".doc")
      ) {
        let response1 = await axios.get(
          `${process.env.REACT_APP_API_SERVER}/rm/b2cpolicies/getSASFile?tenantId=` +
            tenantId +
            "&fileName=" +
            encodeURIComponent(contractDocument) +
            "&referenceNumber=" +
            referenceNumber
        );
        setDocs([{ uri: response1.data, fileName: contractDocument }]);
      } else {
        const blob = base64ToBlob(base64String, contentType);
        const blobUrl = window.URL.createObjectURL(blob);
        setDocs([{ uri: blobUrl, fileName: contractDocument }]);

        setFileType("other");
        //setDocumentUri(reader.result as string);
      }

      //setDocs([{ uri: documentUri }]);
      //setDocs([{ uri: "https://localhost:4000/logo.png" }]);
      setViewerUrl(response.data);
      setViewerOpen(true);
      // You can now create a Blob or handle the base64 string as required
    } else {
      console.error("Invalid data format");
    }
  };
  useEffect(() => {
    console.log("file udated", file); // Access file inside useEffect
  }, [file]);
  const columns = useMemo<MRT_ColumnDef<ServiceProviderRegister>[]>(
    () => [
      {
        accessorKey: "id", //access nested data with dot notation
        header: "System Id",
        enableEditing: false,
        visibleInShowHideMenu: false,
        enableHiding: false,
      },
      // supplier name
      {
        accessorKey: "supplierName", //access nested data with dot notation
        header: "Name",
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              // width: "60vh",
              whiteSpace: "pre-wrap",
              cursor: "pointer", // Add cursor pointer to indicate it's clickable
              textDecoration: "underline", // Add underline to indicate it's a hyperlink
              // color: "black", // Add color to indicate it's a hyperlink
            }}
            onClick={() => {
              console.log("row.orogin.id", row.original.id);
              setSelectedViewRowId(row.original.id);
            }}
          >
            <strong color="#111827">{row.original.supplierName}</strong>
            {/* <span style={{ fontSize: "14px", color: "#66696C" }}>{row.original.systemSummary}</span> */}
          </Box>
        ),
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.supplierName,
          helperText: validationErrors?.supplierName,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              supplierName: undefined,
            }),
          //optionally add validation checking for onBlur or onChange
        },
        enableEditing: true,
      },
      // supplier service type
      {
        accessorKey: "serviceType", //access nested data with dot notation
        header: "Supplier Service Type",
        editVariant: "select",
        editSelectOptions: defaultServiceType,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.serviceType,
          helperText: validationErrors?.serviceType,
          onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            setServiceType(event.target.value);
          },
        },
        enableEditing: true,
      },
      // date contract signed on
      {
        accessorKey: "dateContractSigned", //access nested data with dot notation
        header: "Signed On",
        muiEditTextFieldProps: {
          type: "date",
          required: true,
          InputLabelProps: { shrink: true },
          error: !!validationErrors?.dateContractSigned,
          helperText: validationErrors?.dateContractSigned,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              dateContractSigned: undefined,
            }),
        },
        Cell: ({ cell }) => {
          if (cell.getValue() === null || cell.getValue() === undefined) {
            return "";
          }
          return new Date(cell.getValue<Date>()).toLocaleDateString();
        }, //render Date as a string
        enableEditing: true,
      },
      // contact name
      {
        accessorKey: "contactName", //access nested data with dot notation
        header: "Contact Name",
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.contactName,
          helperText: validationErrors?.contactName,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              contactName: undefined,
            }),
        },
        enableEditing: true,
      },
      // contact phone
      {
        accessorKey: "contactPhone", //access nested data with dot notation
        header: "Contact Phone",
        muiEditTextFieldProps: {
          type: "number",
          required: true,
          error: !!validationErrors?.contactPhone,
          helperText: validationErrors?.contactPhone,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              contactPhone: undefined,
            }),
        },
        enableEditing: true,
      },
      // contact email
      {
        accessorKey: "contactEmail", //access nested data with dot notation
        header: "Contact Email",
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.contactEmail,
          helperText: validationErrors?.contactEmail,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              contactEmail: undefined,
            }),
        },
        enableEditing: true,
      },

      // other service type
      {
        accessorKey: "otherServiceType", //access nested data with dot notation
        header: "Other Service Type",
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.otherServiceType,
          helperText: validationErrors?.otherServiceType,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              otherServiceType: undefined,
            }),
          //optionally add validation checking for onBlur or onChange
        },
        enableEditing: true,
      },
      // service details
      {
        accessorKey: "serviceDetails", //access nested data with dot notation
        header: "Details of Services Provided",
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.serviceDetails,
          helperText: validationErrors?.serviceDetails,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              serviceDetails: undefined,
            }),
        },
        enableEditing: true,
      },

      // date service
      {
        accessorKey: "dateServicesCommenced", //access nested data with dot notation
        header: "Date Service Commenced",
        muiEditTextFieldProps: {
          type: "date",
          required: true,
          InputLabelProps: { shrink: true },
          error: !!validationErrors?.dateServicesCommenced,
          helperText: validationErrors?.dateServicesCommenced,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              dateServicesCommenced: undefined,
            }),
        },
        Cell: ({ cell }) => {
          if (cell.getValue() === null || cell.getValue() === undefined) {
            return "";
          }
          return new Date(cell.getValue<Date>()).toLocaleDateString();
        }, //render Date as a string
        enableEditing: true,
      },

      // contract document upload
      {
        accessorKey: "fileUpload", //access nested data with dot notation
        header: "Contract Document",
        muiEditTextFieldProps: {
          type: "file",
          error: !!validationErrors?.fileUpload,
          helperText: validationErrors?.fileUpload,
          //remove any previous validation errors when user focuses on the input
          onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            //check file extension should be either .docx, .doc, .pdf
            setFile(event.target.files?.[0]);
          },
        },
        //render Date as a string
        enableEditing: true,
        visibleInShowHideMenu: false,
        enableHiding: false,
      },
      // contract document
      {
        accessorKey: "contractDocument", //access nested data with dot notation
        header: "Contract Document",
        muiEditTextFieldProps: {
          type: "text",
          //remove any previous validation errors when user focuses on the input
        },
        Cell: ({ renderedCellValue, row, cell }) => {
          return (
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                handleClick(
                  cell.getValue<string>(),
                  (accounts[0]?.idTokenClaims as IdTokenClaims)?.appTenantId,
                  row.original.id
                );
              }}
            >
              {renderedCellValue}
            </Link>
          );
        },
        enableEditing: true,
      },
      // other vendor information
      {
        accessorKey: "otherVendorInformation", //access nested data with dot notation
        header: "Other Relevant Information",
        muiEditTextFieldProps: {
          type: "text",
          error: !!validationErrors?.otherVendorInformation,
          helperText: validationErrors?.otherVendorInformation,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              otherVendorInformation: undefined,
            }),
        },
        enableEditing: true,
      },
    ],
    [validationErrors, users]
  );
  const handleExportRows = (rows: any) => {
    const doc = new jsPDF();
    //derive column headers and accesorKey from the columns array

    //create a new structure from columns accessorKey
    const colStruct = [
      { accessorKey: "supplierName", header: "Supplier Name" },
      { accessorKey: "serviceType", header: "Service Type" },
      { accessorKey: "serviceDetails", header: "Service Details" },
      { accessorKey: "contactName", header: "Contact Name" },
      { accessorKey: "contactEmail", header: "Contact Email" },

      { accessorKey: "dateServicesCommenced", header: "Date Services Commenced" },
    ];
    //from colStruct get the headers but use columns structure
    const tableHeaders = colStruct.map((col) => col.header);
    //const tableHeaders = columns.map((column) => column.header);
    //from rows.original get the values for each column using colStruct
    const tableData = rows.map((row: any) =>
      colStruct.map((col) => {
        if (col.accessorKey === "dateServicesCommenced") {
          return new Date(row.original[col.accessorKey]).toLocaleDateString("en-AU");
        }
        if (col.accessorKey === "manager") {
          return (
            users?.find((user) => user.id === row.original[col.accessorKey])?.givenName +
            " " +
            users?.find((user) => user.id === row.original[col.accessorKey])?.surname
          );
        }
        return row.original[col.accessorKey];
      })
    );

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
    });

    doc.save("detail-serviceprovider.pdf");
  };
  //READ hook (get users from api)
  function useGetItems() {
    return useQuery<ServiceProviderRegister[]>({
      queryKey: ["ServiceProviderRegister"],
      queryFn: async () => {
        const appTenantId = (accounts[0]?.idTokenClaims as IdTokenClaims)?.appTenantId;
        const userResponse = await axios.get(
          `${process.env.REACT_APP_API_SERVER}/rm/user/getAllUsersForTenant/` + appTenantId
        );
        setUsers((prevValue: Member[]) => [...prevValue, ...userResponse.data]);
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_SERVER}/rm/assetregister/getAllServiceProviderRegisterForTenant/` +
              appTenantId
          );

          // let contractDocument: response.datcontractDocument,
          // let tenantId: string,
          // let referenceNumber: Response.id

          //  response.data &&
          if (response.data && response.data.contractDocument) {
            console.log("response.data.contractDocument", response.data.contractDocument);

            const response1 = await axios.get(
              `${process.env.REACT_APP_API_SERVER}/rm/b2cpolicies/getFile?tenantId=` +
                appTenantId +
                "&fileName=" +
                encodeURIComponent(response.data.contractDocument) +
                "&referenceNumber=" +
                response.data.id
            );

            // response.data.fileUpload = "";
            //setFile(response1);
          }
          setData(response.data);
          return response.data;
        } catch (error: any) {
          // Handle 404 error (Not Found)
          if (error.response && error.response.status === 404) {
            console.error("Data not found:", error.message);
            return []; // Return an empty array or handle as needed
          }

          // Handle other errors
          console.error("Error fetching data:", error.message);
          throw error; // Re-throw the error for React Query to handle
        }
      },
      refetchOnWindowFocus: false,
    });
  }

  function useCreateItem() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (item: ServiceProviderRegister) => {
        //send api update request here
        await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
        return Promise.resolve();
      },
      //client side optimistic update
      onMutate: async (newItemInfo: ServiceProviderRegister) => {
        let response = await axios.post(
          `${process.env.REACT_APP_API_SERVER}/rm/assetregister/createServiceProviderRegister`,
          {
            tenantId: (accounts[0].idTokenClaims as IdTokenClaims).appTenantId,
            supplierName: newItemInfo.supplierName,
            serviceType: newItemInfo.serviceType,
            serviceDetails: newItemInfo.serviceDetails,
            contactName: newItemInfo.contactName,
            contactEmail: newItemInfo.contactEmail,
            contactPhone: newItemInfo.contactPhone,
            dateServicesCommenced: newItemInfo.dateServicesCommenced,
            dateContractSigned: newItemInfo.dateContractSigned,
            contractDocument: file ? file.name : "",
            otherVendorInformation: newItemInfo.otherVendorInformation,
            otherServiceType: newItemInfo.otherServiceType,
          }
        );
        if (response.status !== 201) {
          alert("Error creating system");
          return;
        }

        let itemId = (await response).data.id;
        if (file) {
          const fileFormData = new FormData();
          fileFormData.append("file", file);
          // if (file.length > 0) {
          // const fileFormData = new FormData();
          // file.forEach((file: string | Blob, index: any) => {
          //   fileFormData.append(`file${index}`, file); // Append each file with a unique key
          // });
          fileFormData.append("referenceNumber", itemId);
          // itemId.forEach((itemId: string | Blob) => {
          //   fileFormData.append("referenceNumber", itemId); // Append reference number for each file
          // });

          try {
            let response = await axios.post(
              `${process.env.REACT_APP_API_SERVER}/rm/b2cpolicies/uploadFiles/${
                (accounts[0].idTokenClaims as IdTokenClaims).appTenantId
              }`,
              fileFormData
            );

            if (response.status !== 200) {
              alert("Error uploading file");
              return;
            } else {
              alert("File uploaded successfully!");
            }
          } catch (error: any) {
            console.error(
              "Error uploading file:",
              error.response ? error.response.data : error.message
            );
            alert("Error uploading file: " + error.response ? error.response.data : error.message);
          }
        }
        queryClient.setQueryData(
          ["ServiceProviderRegister"],
          (prevItems: ServiceProviderRegister[] | undefined) => {
            const newItems = [
              ...(prevItems || []), // Use an empty array if prevItems is undefined or not iterable
              {
                ...newItemInfo,
                id: itemId,
                contractDocument: file ? file.name : "",
                fileUpload: "",
              },
            ];
            console.log("New items", newItems);
            return newItems as ServiceProviderRegister[];
          }
        );
        setFile(null);
      },
      // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
  }
  //UPDATE action
  const handleSaveItem: MRT_TableOptions<ServiceProviderRegister>["onEditingRowSave"] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateItem(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await updateItem(values);
    table.setEditingRow(null); //exit editing mode
    setFile(null);
  };
  //DELETE action
  const openDeleteConfirmModal = (row: MRT_Row<ServiceProviderRegister>) => {
    deleteItem(row.original.id);
    handleClose();
  };

  // delete action
  const [anchordEl, setAnchordEl] = React.useState<null | HTMLElement>(null);
  const deleteOpen = Boolean(anchordEl);

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchordEl(event.currentTarget);
  };

  const handleDeleteClose = () => {
    setAnchordEl(null);
  };
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 450,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedRowObject(null);
    setSelectedViewRowId("");
    setDeleteRowId("");
  };

  const onDrop = async (acceptedFiles: any) => {
    console.log(acceptedFiles);

    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
    // Check if the file with the same name already exists
    // const isDuplicate = file.some((file: any) => file.name === acceptedFiles[0].name);
    // if (!isDuplicate) {
    //   // If it's not a duplicate, add the file to the files array
    //   setFile([...file, acceptedFiles[0]]);
    // } else {
    //   console.log("File with the same name already exists!");
    //   // If it's a duplicate, set the error message
    //   // setErrorMessage("File with the same name already exists!");
    // }
  };

  const [selectedViewRowId, setSelectedViewRowId] = useState<string>("");
  const [selectedRowObject, setSelectedRowObject] = useState<ServiceProviderRegister | null>(null); // Specify type explicitly

  //UPDATE hook (put user in api)
  function useUpdateItem() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (item: ServiceProviderRegister) => {
        //send api update request here
        await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
        return Promise.resolve();
      },
      //client side optimistic update
      onMutate: async (newItemInfo: ServiceProviderRegister) => {
        newItemInfo.fileUpload = "";
        newItemInfo.contractDocument = file ? file.name : newItemInfo.contractDocument;
        if (file) {
          const fileFormData = new FormData();
          fileFormData.append("file", file);
          // if (file.length > 0) {
          // const fileFormData = new FormData();
          // file.forEach((file: string | Blob, index: any) => {
          //   fileFormData.append(`file${index}`, file); // Append each file with a unique key
          // });
          fileFormData.append("referenceNumber", newItemInfo.id);
          // itemId.forEach((itemId: string | Blob) => {
          //   fileFormData.append("referenceNumber", itemId); // Append reference number for each file
          // });

          try {
            let response = await axios.post(
              `${process.env.REACT_APP_API_SERVER}/rm/b2cpolicies/uploadFiles/${
                (accounts[0].idTokenClaims as IdTokenClaims).appTenantId
              }`,
              fileFormData
            );

            if (response.status !== 200) {
              alert("Error uploading file");
              return;
            } else {
              alert("File uploaded successfully!");
            }
          } catch (error: any) {
            console.error(
              "Error uploading file:",
              error.response ? error.response.data : error.message
            );
            alert("Error uploading file: " + error.response ? error.response.data : error.message);
          }
        }
        let response = axios.put(
          `${process.env.REACT_APP_API_SERVER}/rm/assetregister/saveServiceProviderRegister`,
          {
            tenantId: (accounts[0].idTokenClaims as IdTokenClaims).appTenantId,
            id: newItemInfo.id,
            supplierName: newItemInfo.supplierName,
            serviceType: newItemInfo.serviceType,
            serviceDetails: newItemInfo.serviceDetails,
            contactName: newItemInfo.contactName,
            contactEmail: newItemInfo.contactEmail,
            contactPhone: newItemInfo.contactPhone,
            dateServicesCommenced: newItemInfo.dateServicesCommenced,
            dateContractSigned: newItemInfo.dateContractSigned,
            contractDocument: newItemInfo.contractDocument,
            otherVendorInformation: newItemInfo.otherVendorInformation,
            isDeleted: newItemInfo.isDeleted,
            otherServiceType: newItemInfo.otherServiceType,
          }
        );

        setFile(null);
        queryClient.setQueryData(["ServiceProviderRegister"], (prevUsers: any) =>
          prevUsers?.map((prevUser: ServiceProviderRegister) =>
            prevUser.id === newItemInfo.id ? newItemInfo : prevUser
          )
        );
      },
      // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
  }
  //DELETE hook (delete user in api)
  function useDeleteItem() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (id: string) => {
        //send api update request here
        await new Promise<void>((resolve) => {
          let response = axios.delete(
            `${
              process.env.REACT_APP_API_SERVER
            }/rm/assetregister/deleteServiceProviderRegister?tenantId=${
              (accounts[0].idTokenClaims as IdTokenClaims).appTenantId
            }&id=${id}`
          );
          resolve(); // Resolve the outer promise
        }); //fake api call
        return Promise.resolve();
      },
      //client side optimistic update
      onMutate: (id: string) => {
        queryClient.setQueryData(["ServiceProviderRegister"], (prevUsers: any) =>
          prevUsers?.filter((item: ServiceProviderRegister) => item.id !== id)
        );
      },
      // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
  }
  //call CREATE hook
  const { mutateAsync: createItem, isPending: isCreatingItem } = useCreateItem();
  //call READ hook
  const {
    data: fetchedItems = [],
    isError: isLoadingItemsError,
    isFetching: isFetchingItems,
    isLoading: isLoadingITems,
  } = useGetItems();
  const headers = Object.keys(extractedHeaders);

  // Convert data to the required format (array of arrays) with dynamic column mapping
  const convertedData = [
    headers,
    ...fetchedItems.map((record: ServiceProviderRegister) =>
      headers.map((header: string) => {
        const key = extractedHeaders[header as keyof typeof extractedHeaders];
        if (key === "contactPhone") {
          return ('=""' + record[key as keyof ServiceProviderRegister] + '""') as string;
        } else {
          return record[key as keyof ServiceProviderRegister] as string;
        }
      })
    ),
  ];
  // Convert data to the required format (array of arrays) with dynamic column mapping

  // Effect to update selectedRowObject when selectedViewRowId changes
  useEffect(() => {
    if (selectedViewRowId !== "") {
      // Find the object in data array with the matching id
      const selectedObject = fetchedItems.find((obj) => obj.id === selectedViewRowId) || null; // Provide default value of null
      setSelectedRowObject(selectedObject);
    } else {
      setSelectedRowObject(null); // Reset selectedRowObject if selectedViewRowId is empty
    }
  }, [selectedViewRowId, fetchedItems]);
  //call UPDATE hook
  const { mutateAsync: updateItem, isPending: isUpdatingItem } = useUpdateItem();
  //call DELETE hook
  const { mutateAsync: deleteItem, isPending: isDeletingItem } = useDeleteItem();
  //CREATE action
  const handleCreateItem: MRT_TableOptions<ServiceProviderRegister>["onCreatingRowSave"] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateItem(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await createItem(values);
    table.setCreatingRow(null); //exit creating mode
  };
  const table = useMaterialReactTable({
    columns,
    data: fetchedItems, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    createDisplayMode: "modal", //default ('row', and 'custom' are also available)
    editDisplayMode: "modal", //default ('row', 'cell', 'table', and 'custom' are also available)
    enableEditing: true,
    positionActionsColumn: "last",
    enableTopToolbar: false,

    getRowId: (row) => row.id,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
      boxShadow: "none !important",
      borderRadius: "none !important",
    }),
    muiTableBodyRowProps: {
      hover: false,
      sx: {
        whiteSpace: "nowrap",
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none !important",
        // width: "1187px",
        // minWidth: "1187px",
      },
    },
    muiTableProps: {
      sx: {
        // border: "1px solid rgba(81, 81, 81, .5)",
        boxShadow: "none !important",
        whiteSpace: "nowrap",
        borderRadius: "none !important",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #EDEDED",
        // borderLeft: "1px solid rgba(81, 81, 81, .5)",
        // borderTop: "1px solid rgba(81, 81, 81, .5)",
        // fontStyle: "italic",
        fontWeight: "bold",
        fontSize: "14px",
        whiteSpace: "nowrap",
        textAlign: "center",
        boxShadow: "none",
        paddingLeft: "20px",
        backgroundColor: "#FBFBFF",
        color: "#111827 60%",
        height: "50px",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: "1px solid rgba(81, 81, 81, .5)",
        // whiteSpace: "pre-wrap",
        // paddingLeft: "20px",
        // paddingRight: "20px",
        paddingTop: "10px",
        paddingBottom: "10px",
        border: "1px solid #EDEDED",
        // borderLeft: columns.accessorKey === "status" ? "none" : "1px solid #EDEDED",
      },
    },
    muiToolbarAlertBannerProps: isLoadingItemsError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    muiTableContainerProps: {
      sx: {
        minHeight: "100px",
      },
    },
    // renderTopToolbarCustomActions: ({ table }) => (
    //   <>
    //     <Button
    //       variant="contained"
    //       onClick={() => {
    //         // handleAddClick();
    //         table.setCreatingRow(true); //simplest way to open the create row modal with no default values
    //         //or you can pass in a row object to set default values with the `createRow` helper function
    //         // table.setCreatingRow(
    //         //   createRow(table, {
    //         //     //optionally pass in default values for the new row, useful for nested data or other complex scenarios
    //         //   }),
    //         // );
    //       }}
    //     >
    //       Add Provider
    //     </Button>
    //   </>
    // ),
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateItem,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleSaveItem,
    //optionally customize modal content
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => {
      return (
        <>
          <Dialog
            open={true}
            onClose={() => table.setEditingRow(null)}
            fullWidth={true}
            maxWidth={"lg"}
          >
            <DialogTitle variant="h3">
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontSize="16px" fontWeight="600">
                  Edit I.T Service Provider
                </Typography>
                <RRM_EditActionButtons variant="icon" table={table} row={row} />
              </Box>
              <Divider sx={{ marginTop: "1rem" }} />
            </DialogTitle>
            <DialogContent
              sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}
            >
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="id"
                  table={table}
                  disabled={true}
                  style={{ display: "none" }} //still have to put edit field else id is not sent in update
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="supplierName"
                  labelName="Supplier Name"
                  table={table}
                  inputProps={{ maxLength: 200 }}
                />
                <RRM_EditCellTextField row={row} accessKey="serviceType" table={table} />
                {(row._valuesCache.serviceType === "Other" && (
                  <RRM_EditCellTextField row={row} accessKey="otherServiceType" table={table} />
                )) || <></>}
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="serviceDetails"
                  tooltip="Information about the type of service provided, e.g. for an I.T. Support Services Provider this might include setting up laptops."
                  table={table}
                  inputProps={{ maxLength: 10000 }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="contactName"
                  table={table}
                  inputProps={{ maxLength: 100 }}
                />
                <RRM_EditCellTextField row={row} accessKey="contactEmail" table={table} />
                <RRM_EditCellTextField row={row} accessKey="contactPhone" table={table} />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField row={row} accessKey="dateServicesCommenced" table={table} />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="dateContractSigned"
                  labelName="Date Contract Signed"
                  table={table}
                />
                {/* <RRM_EditCellTextField row={row} accessKey="fileUpload" table={table} /> */}
                {/* file upload */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "left",
                    gap: "5px",
                  }}
                >
                  <LightTooltip
                    title="Upload any documents relating to your contract with the I.T. Services Provider, e.g. documents outlining any Service Level Agreements that have been agreed to."
                    arrow
                    // placement="bottom"
                  >
                    <Typography
                      fontSize="14px"
                      color="rgba(17, 24, 39, 0.6)"
                      fontWeight="600"
                      sx={{ marginLeft: "5px", cursor: "pointer" }}
                    >
                      Contract Upload
                    </Typography>
                  </LightTooltip>
                  <TextField
                    fullWidth
                    variant="outlined"
                    // label="Action Description"
                    placeholder="Contract Upload"
                    name="fileUpload"
                    multiline
                    type="text" // You may need to determine the type dynamically based on your data
                    value={(row.original.contractDocument || file?.name) ?? ""}
                    // onChange={formik.handleChange}
                    // onBlur={formik.handleBlur}
                    sx={{
                      height: "44px",
                      width: "400px",
                      borderRadius: "5px",
                      paddingY: "0px",
                    }}
                    InputProps={{
                      style: { fontSize: "14px", height: "44px", borderRadius: "5px" }, // Set font size here
                      endAdornment: (
                        <InputAdornment position="end">
                          {row.original.contractDocument ? (
                            <IconButton
                              onClick={async () => {
                                try {
                                  let res = await axios.delete(
                                    `${
                                      process.env.REACT_APP_API_SERVER
                                    }/rm/b2cpolicies/deleteFile?tenantId=${
                                      (accounts[0].idTokenClaims as IdTokenClaims).appTenantId
                                    }&fileName=${encodeURIComponent(
                                      row.original.contractDocument
                                    )}&referenceNumber=${row.original.id}`
                                  );
                                } catch (err) {
                                  console.log(err);
                                }
                                row._valuesCache.contractDocument = "";
                                row.original.contractDocument = "";
                                row._valuesCache.fileUpload = "";
                                setFile(null);
                              }}
                            >
                              <Delete />
                            </IconButton>
                          ) : (
                            <Dropzone
                              onDrop={onDrop}
                              accept={{
                                "application/pdf": [".pdf"],
                                "image/*": [],
                                "application/msword": [],
                                "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                                  [],
                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                                  [],
                                "application/vnd.ms-excel": [],
                              }}
                            >
                              {({ getRootProps, getInputProps }) => (
                                <div {...getRootProps()}>
                                  <input
                                    {...getInputProps()}
                                    id="fileInput"
                                    // onChange={setFile(event.target.files?.[0])}
                                    style={{ display: "none" }}
                                  />
                                  <IconButton
                                  // onClick={handleUploadClick}
                                  >
                                    <Upload />
                                  </IconButton>
                                </div>
                              )}
                            </Dropzone>
                          )}
                          {/* <IconButton>
                                    <Download />
                                  </IconButton>
                                  <IconButton>
                                    <Delete />
                                  </IconButton> */}

                          {/* <IconButton>
                                    <Download />
                                  </IconButton>
                                  <IconButton>
                                    <Delete />
                                  </IconButton> */}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="otherVendorInformation"
                  table={table}
                  inputProps={{ maxLength: 20000 }}
                />
              </Box>
              {/* or render custom edit components here */}
            </DialogContent>
            {/* <DialogActions>
              <MRT_EditActionButtons variant="text" table={table} row={row} />
            </DialogActions> */}
          </Dialog>
        </>
      );
    },
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => {
      return (
        <>
          <Dialog
            open={true}
            onClose={() => {
              table.setEditingRow(null);
              setFile(null);
            }}
            fullWidth={true}
            maxWidth={"lg"}
          >
            {" "}
            <DialogTitle variant="h3">
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontSize="16px" fontWeight="600">
                  Add I.T Service Provider
                </Typography>
                <RRM_EditActionButtons variant="icon" table={table} row={row} />
              </Box>
              <Divider sx={{ marginTop: "1rem" }} />
            </DialogTitle>
            <DialogContent
              sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}
            >
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="id"
                  table={table}
                  disabled={true}
                  style={{ display: "none" }} //still have to put edit field else id is not sent in update
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="supplierName"
                  labelName="Supplier Name"
                  table={table}
                  inputProps={{ maxLength: 200 }}
                />
                <RRM_EditCellTextField row={row} accessKey="serviceType" table={table} />
                {(serviceType === "Other" && (
                  <RRM_EditCellTextField row={row} accessKey="otherServiceType" table={table} />
                )) || <></>}
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="serviceDetails"
                  tooltip="Information about the type of service provided, e.g. for an I.T. Support Services Provider this might include setting up laptops."
                  table={table}
                  inputProps={{ maxLength: 10000 }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="contactName"
                  table={table}
                  inputProps={{ maxLength: 100 }}
                />
                <RRM_EditCellTextField row={row} accessKey="contactEmail" table={table} />
                <RRM_EditCellTextField row={row} accessKey="contactPhone" table={table} />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField row={row} accessKey="dateServicesCommenced" table={table} />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="dateContractSigned"
                  labelName="Date Contract Signed"
                  table={table}
                />
                {/* <RRM_EditCellTextField row={row} accessKey="fileUpload" table={table} /> */}
                {/* file upload */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "left",
                    gap: "5px",
                  }}
                >
                  <LightTooltip
                    title="Upload any documents relating to your contract with the I.T. Services Provider, e.g. documents outlining any Service Level Agreements that have been agreed to."
                    arrow
                    // placement="bottom"
                  >
                    <Typography
                      fontSize="14px"
                      color="rgba(17, 24, 39, 0.6)"
                      fontWeight="600"
                      sx={{ marginLeft: "5px", cursor: "pointer" }}
                    >
                      Contract Upload
                    </Typography>
                  </LightTooltip>
                  <TextField
                    fullWidth
                    variant="outlined"
                    // label="Action Description"
                    placeholder="Contract Upload"
                    name="fileUpload"
                    multiline
                    type="text" // You may need to determine the type dynamically based on your data
                    value={file ? `1 attached` : ""}
                    // onChange={formik.handleChange}
                    // onBlur={formik.handleBlur}
                    sx={{
                      height: "44px",
                      width: "400px",
                      borderRadius: "5px",
                      paddingY: "0px",
                    }}
                    InputProps={{
                      style: { fontSize: "14px", height: "44px", borderRadius: "5px" }, // Set font size here
                      endAdornment: (
                        <InputAdornment position="end">
                          {file ? (
                            <IconButton onClick={() => setFile(null)}>
                              <Delete />
                            </IconButton>
                          ) : (
                            <Dropzone
                              onDrop={onDrop}
                              accept={{
                                "application/pdf": [".pdf"],
                                "image/*": [],
                                "application/msword": [],
                                "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                                  [],
                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                                  [],
                                "application/vnd.ms-excel": [],
                              }}
                            >
                              {({ getRootProps, getInputProps }) => (
                                <div {...getRootProps()}>
                                  <input
                                    {...getInputProps()}
                                    id="fileInput"
                                    // onChange={setFile(event.target.files?.[0])}
                                    style={{ display: "none" }}
                                  />
                                  <IconButton>
                                    <Upload />
                                  </IconButton>
                                </div>
                              )}
                            </Dropzone>
                          )}

                          {/* <IconButton>
                                        <Download />
                                      </IconButton>
                                      <IconButton>
                                        <Delete />
                                      </IconButton> */}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="otherVendorInformation"
                  table={table}
                  inputProps={{ maxLength: 20000 }}
                />
              </Box>
            </DialogContent>
            {/* <DialogActions>
              <MRT_EditActionButtons variant="text" table={table} row={row} />
            </DialogActions> */}
          </Dialog>
        </>
      );
    },
    // renderToolbarInternalActions: ({ table }) => (
    //   <Box sx={{ display: "flex", gap: "20px", alignItems: "center", paddingRight: "10px" }}>
    //     <Button
    //       variant="contained"
    //       onClick={() => {
    //         table.setCreatingRow(true); //simplest way to open the create row modal with no default values
    //       }}
    //     >
    //       Add Provider
    //     </Button>
    //     <Button startIcon={<Export />}>
    //       <CSVLink data={convertedData} filename={"Service Provider.csv"}>
    //         <Typography fontSize="14px" color="#233DFF" fontWeight="bold">
    //           Export Data
    //         </Typography>
    //       </CSVLink>
    //     </Button>
    //   </Box>
    // ),
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "2px" }}>
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            sx={{ padding: "2px" }}
            onClick={(e) => {
              setDeleteRowId(row.original.id);
              handleDeleteClick(e);
            }}
          >
            <MoreOption />
          </IconButton>
        </Tooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchordEl}
          open={deleteOpen}
          onClose={() => handleDeleteClose()}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem
            onClick={() => {
              // setDeleteRowId(row.original.id);
              // setConfirmOpen(true);
              handleDeleteClose();
              handleOpen();
            }}
          >
            Delete
          </MenuItem>
        </Menu>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography fontSize="17px" fontWeight="bold" id="modal-modal-title">
              Are you sure you want to delete this Service Provider?
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: "2rem",
                mt: "1rem",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{ color: "white", backgroundColor: "red" }}
                onClick={() => {
                  deleteItem(deleteRowId);
                  handleClose();
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    ),
    state: {
      isLoading: isLoadingITems,
      isSaving: isCreatingItem || isUpdatingItem || isDeletingItem,
      showAlertBanner: isLoadingItemsError,
      showProgressBars: isFetchingItems,
    },
    enableColumnActions: false,
    initialState: {
      //optional initial state
      columnVisibility: {
        id: false,
        fileUpload: false,
        otherServiceType: false,
        // serviceType: false,
        serviceDetails: false,
        dateServicesCommenced: false,
        //contractDocument: false,
        otherVendorInformation: false,
      },
    },
  });

  return (
    <>
      {data ? (
        <div>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "20px",
              alignItems: "center",
              paddingRight: "10px",
              position: "relative",
              top: "-35px",
              // width: "826px",
              // left: "68vh",
              marginLeft: "428px",
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                table.setCreatingRow(true);
              }}
            >
              Add Provider
            </Button>
            <Button startIcon={<Export />}>
              <CSVLink data={convertedData} filename={"Service Provider.csv"}>
                <Typography fontSize="14px" color="#233DFF" fontWeight="bold">
                  Export Data
                </Typography>
              </CSVLink>
            </Button>
          </Box>
          <MaterialReactTable table={table} />
          {selectedViewRowId !== "" && (
            <ViewAction action={selectedRowObject} type="service" onClose={handleClose} />
          )}
          <Dialog
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
            open={viewerOpen}
            onClose={handleViewerClose}
            className={classes.paper}
            style={modalStyle}
            maxWidth="lg"
          >
            <DialogContent>
              <Box>
                {/*                 <div className="document-container">
                  <div className="footer">
                    <div className="button-container">
                      <Button onClick={handleZoomIn} disabled={pageScale >= 3}>
                        Zoom +
                      </Button>
                      <Button onClick={handleZoomOut} disabled={pageScale <= 0.3}>
                        Zoom -
                      </Button>
                    </div>
                    <div className="page-text">
                      Page {pageNumber} of {numPages}
                    </div>
                    <div className="button-container">
                      <Button onClick={handlePrevious} disabled={pageNumber === 1}>
                        ‹ Previous
                      </Button>
                      <Button onClick={handleNext} disabled={pageNumber === numPages}>
                        Next ›
                      </Button>
                    </div>
                  </div>
                  <div className="page-container">
                    <Document
                      file={viewerUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      options={options}
                    >
                      <Page pageNumber={pageNumber} scale={pageScale} />
                    </Document>
                  </div>
                </div>
 */}{" "}
                {fileType !== "html" ? (
                  <DocViewer
                    documents={docs}
                    config={{
                      header: {
                        disableHeader: false,
                        disableFileName: false,
                        retainURLParams: false,
                      },
                      csvDelimiter: ",", // "," as default,
                      pdfZoom: {
                        defaultZoom: 1.1, // 1 as default,
                        zoomJump: 0.2, // 0.1 as default,
                      },
                      // pdfVerticalScrollByDefault: false, // false as default
                    }}
                    style={{ width: 600, height: 800 }}
                    pluginRenderers={DocViewerRenderers}
                  />
                ) : (
                  /*                   <div
                    dangerouslySetInnerHTML={{
                      __html: documentUri,
                    }}
                    style={{ width: 600, height: 800 }}
                  ></div>
 */ <DocViewer
                    documents={docs}
                    config={{
                      header: {
                        disableHeader: false,
                        disableFileName: false,
                        retainURLParams: false,
                      },
                      csvDelimiter: ",", // "," as default,
                      pdfZoom: {
                        defaultZoom: 1.1, // 1 as default,
                        zoomJump: 0.2, // 0.1 as default,
                      },
                      // pdfVerticalScrollByDefault: false, // false as default
                    }}
                    style={{ width: 600, height: 800 }}
                    pluginRenderers={DocViewerRenderers}
                  />
                )}
              </Box>
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        <p>Loading, please wait...</p>
      )}
    </>
  );
};

const ServiceProviderRegisterWithProviders = () => (
  //Put this with your other react-query providers near root of your app
  <QueryClientProvider client={queryClient}>
    <ServiceProviderRegister />
  </QueryClientProvider>
);
export default ServiceProviderRegisterWithProviders;
